'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, FileText, Wrench, HelpCircle, BookOpen, ArrowRight, Loader2 } from 'lucide-react';
import MiniSearch from 'minisearch';

interface SearchDoc {
  id: string;
  type: 'blog' | 'tool' | 'faq' | 'page';
  title: string;
  description: string;
  content: string;
  url: string;
  category: string;
  date: string;
}

interface SearchResult extends SearchDoc {
  score: number;
}

const typeIcons: Record<string, typeof FileText> = {
  blog: BookOpen,
  tool: Wrench,
  faq: HelpCircle,
  page: FileText,
};

const typeLabels: Record<string, Record<string, string>> = {
  blog: { nl: 'Blog', en: 'Blog', de: 'Blog', fr: 'Blog' },
  tool: { nl: 'Tool', en: 'Tool', de: 'Tool', fr: 'Outil' },
  faq: { nl: 'Veelgestelde vraag', en: 'FAQ', de: 'FAQ', fr: 'FAQ' },
  page: { nl: 'Pagina', en: 'Page', de: 'Seite', fr: 'Page' },
};

const i18n: Record<string, { placeholder: string; noResults: string; searching: string; resultCount: string }> = {
  nl: {
    placeholder: 'Zoek in artikelen, tools en meer...',
    noResults: 'Geen resultaten gevonden. Probeer andere zoektermen.',
    searching: 'Zoeken...',
    resultCount: 'resultaten',
  },
  en: {
    placeholder: 'Search articles, tools and more...',
    noResults: 'No results found. Try different search terms.',
    searching: 'Searching...',
    resultCount: 'results',
  },
  de: {
    placeholder: 'Artikel, Tools und mehr durchsuchen...',
    noResults: 'Keine Ergebnisse gefunden. Versuchen Sie andere Suchbegriffe.',
    searching: 'Suche...',
    resultCount: 'Ergebnisse',
  },
  fr: {
    placeholder: 'Rechercher des articles, outils et plus...',
    noResults: 'Aucun résultat trouvé. Essayez d\'autres termes.',
    searching: 'Recherche...',
    resultCount: 'résultats',
  },
};

export function SearchClient({ locale }: { locale: string }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [searched, setSearched] = useState(false);
  const miniSearchRef = useRef<MiniSearch<SearchDoc> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const t = i18n[locale] || i18n.en;

  // Load search index
  useEffect(() => {
    async function loadIndex() {
      try {
        const res = await fetch(`/${locale}/search-index.json`);
        const docs: SearchDoc[] = await res.json();

        const ms = new MiniSearch<SearchDoc>({
          fields: ['title', 'description', 'content'],
          storeFields: ['id', 'type', 'title', 'description', 'url', 'category', 'date'],
          searchOptions: {
            boost: { title: 3, description: 2 },
            fuzzy: 0.2,
            prefix: true,
          },
        });

        ms.addAll(docs);
        miniSearchRef.current = ms;
      } catch {
        // Index not available
      } finally {
        setLoading(false);
      }
    }

    loadIndex();
  }, [locale]);

  // Read initial query from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q) {
      setQuery(q);
    }
  }, []);

  // Search when query changes
  const doSearch = useCallback((q: string) => {
    if (!miniSearchRef.current || !q.trim()) {
      setResults([]);
      setSearched(q.trim().length > 0);
      return;
    }

    const searchResults = miniSearchRef.current.search(q, {
      boost: { title: 3, description: 2 },
      fuzzy: 0.2,
      prefix: true,
    }) as unknown as SearchResult[];

    setResults(searchResults);
    setSearched(true);

    // Update URL without navigation
    const url = new URL(window.location.href);
    url.searchParams.set('q', q);
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Debounce search
  useEffect(() => {
    if (loading) return;

    const timer = setTimeout(() => {
      doSearch(query);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, loading, doSearch]);

  // Focus input on load
  useEffect(() => {
    if (!loading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loading]);

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const typeOrder = ['blog', 'tool', 'faq', 'page'];

  return (
    <div className="mx-auto max-w-3xl">
      {/* Search Input */}
      <div className="relative mb-8">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          ) : (
            <Search className="h-5 w-5 text-slate-400" />
          )}
        </div>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.placeholder}
          className="w-full rounded-2xl border-2 border-slate-200 bg-white py-4 pl-12 pr-4 text-lg text-slate-900 placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          autoComplete="off"
        />
      </div>

      {/* Result count */}
      {searched && !loading && (
        <p className="mb-6 text-sm text-slate-500">
          {results.length} {t.resultCount}
        </p>
      )}

      {/* Results */}
      {searched && results.length === 0 && !loading && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
          <Search className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-600">{t.noResults}</p>
        </div>
      )}

      {typeOrder.map((type) => {
        const items = grouped[type];
        if (!items || items.length === 0) return null;
        const Icon = typeIcons[type] || FileText;
        const label = typeLabels[type]?.[locale] || type;

        return (
          <div key={type} className="mb-8">
            <div className="mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4 text-brand-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                {label}
              </h2>
              <span className="text-xs text-slate-400">({items.length})</span>
            </div>
            <div className="space-y-3">
              {items.map((result) => (
                <a
                  key={result.id}
                  href={result.url}
                  className="group block rounded-xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 group-hover:text-brand-700 line-clamp-1">
                        {result.title}
                      </h3>
                      <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                        {result.description}
                      </p>
                      {result.date && (
                        <span className="mt-2 inline-block text-xs text-slate-400">
                          {new Date(result.date).toLocaleDateString(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 flex-shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-brand-500" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
